import { applyI18n } from '../i18n';
import { markdownToHtml } from '../files/markdown';
import { TEXT_PREVIEW_LIMIT } from '../files/fileTypes';
import createDOMPurify, { type DOMPurify as DomPurifyInstance } from 'dompurify';

let purifyInstance: DomPurifyInstance | null = null;

function getPurify(): DomPurifyInstance {
  if (purifyInstance) return purifyInstance;
  if (typeof window === 'undefined') {
    throw new Error('DOMPurify requires a browser DOM');
  }
  purifyInstance = createDOMPurify(window);
  purifyInstance.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'src' && data.attrName !== 'href' && data.attrName !== 'xlink:href') {
      return;
    }
    const value = String(data.attrValue ?? '').trim().toLowerCase();
    if (value.startsWith('data:') && !value.startsWith('data:image/')) {
      data.keepAttr = false;
    }
  });
  return purifyInstance;
}

type TextViewMode = 'rendered' | 'source';

export async function renderRichTextPreview(
  content: HTMLElement,
  blob: Blob,
  mime: string,
): Promise<void> {
  const raw = await blob.text();
  const text = raw.slice(0, TEXT_PREVIEW_LIMIT);
  const isMarkdown = mime === 'text/markdown';

  const wrap = document.createElement('div');
  wrap.className = 'file-preview-rich-text';

  const toolbar = document.createElement('div');
  toolbar.className = 'file-preview-rich-text-toolbar';
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'button ghost';
  toggle.dataset.i18n = 'files.preview.text.viewSource';
  toolbar.append(toggle);

  const viewport = document.createElement('div');
  viewport.className = 'file-preview-rich-text-viewport';

  let mode: TextViewMode = 'rendered';

  const render = () => {
    viewport.replaceChildren();
    if (mode === 'source') {
      const pre = document.createElement('pre');
      pre.className = 'file-preview-text';
      pre.textContent = text;
      viewport.append(pre);
      toggle.dataset.i18n = 'files.preview.text.viewRendered';
    } else {
      const iframe = document.createElement('iframe');
      iframe.className = 'file-preview-document';
      iframe.sandbox = '';
      iframe.referrerPolicy = 'no-referrer';
      iframe.srcdoc = isMarkdown ? markdownToHtml(text) : sanitizeHtmlDocument(text);
      viewport.append(iframe);
      toggle.dataset.i18n = 'files.preview.text.viewSource';
    }
    applyI18n(toolbar);
  };

  toggle.addEventListener('click', () => {
    mode = mode === 'rendered' ? 'source' : 'rendered';
    render();
  });

  wrap.append(toolbar, viewport);
  content.append(wrap);
  render();
}

function sanitizeHtmlDocument(html: string): string {
  const cleaned = getPurify().sanitize(html, {
    WHOLE_DOCUMENT: true,
    FORBID_TAGS: ['script', 'base', 'object', 'embed', 'iframe', 'form'],
    FORBID_ATTR: ['srcdoc'],
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
  if (/<html[\s>]/i.test(cleaned)) return cleaned;
  return `<!doctype html><html><head><meta charset="utf-8"></head><body>${cleaned}</body></html>`;
}

export async function renderPlainTextPreview(content: HTMLElement, blob: Blob, mime: string): Promise<void> {
  const raw = await blob.text();
  let text = raw;
  if (mime === 'application/json' || mime.endsWith('+json')) {
    try {
      text = JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      text = raw;
    }
  }
  const pre = document.createElement('pre');
  pre.className = 'file-preview-text';
  pre.textContent = text.slice(0, TEXT_PREVIEW_LIMIT);
  content.append(pre);
}
